import clsx from "clsx";
import { useState } from "react";

import { Icon } from "~/assets/icons/Icon";
import { Option, Select } from "~/components/ui/select/Select";

import Button from "../../mantine/button/Button";
import Spinner from "../spinner/Spinner";
import { PageJumpPopper } from "./PageJumpPopper";
import styles from "./Pagination.module.scss";

export interface PaginationConfig {
  currentPage: number;
  itemsPerPage: number;
  nextPage?: number | null;
  prevPage?: number | null;
  totalPages?: number;
  totalItems: number;
  disabled?: boolean;
}

interface PaginationProps extends PaginationConfig {
  onPageChange: (page: number) => void;
  onItemsPerPageChange?: (items: number) => void;
  isLoading?: boolean;
  hideItemsPerPage?: boolean;
}
const options = [{ label: "10" }, { label: "25" }, { label: "50" }];

export const Pagination = ({
  currentPage,
  itemsPerPage,
  totalItems,
  onPageChange,
  onItemsPerPageChange,
  isLoading = false,
  hideItemsPerPage = false,
}: PaginationProps) => {
  const [isPopperOpen, setIsPopperOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startItem = (currentPage - 1) * itemsPerPage + 1;
  const endItem = Math.min(currentPage * itemsPerPage, totalItems);

  const handlePrevClick = () => {
    if (currentPage > 1 && !isLoading) {
      onPageChange(currentPage - 1);
    }
  };

  const handleNextClick = () => {
    if (currentPage < totalPages && !isLoading) {
      onPageChange(currentPage + 1);
    }
  };

  const handleFirstClick = () => {
    if (currentPage > 1 && !isLoading) {
      onPageChange(1);
    }
  };

  const handleLastClick = () => {
    if (currentPage < totalPages && !isLoading) {
      onPageChange(totalPages);
    }
  };

  const handleCurrentPageClick = (
    event: React.MouseEvent<HTMLButtonElement>,
  ) => {
    if (!isLoading && totalPages > 1) {
      setAnchorEl(event.currentTarget);
      setIsPopperOpen(true);
    }
  };

  const handlePopperClose = () => {
    setIsPopperOpen(false);
    setAnchorEl(null);
  };

  const handleGoToPage = (page: number) => {
    onPageChange(page);
    handlePopperClose();
  };

  return (
    <div
      className={styles.pagination}
      role="navigation"
      aria-label="Pagination"
    >
      <div className={styles.controls}>
        <Button
          onClick={handleFirstClick}
          disabled={currentPage === 1 || isLoading}
          aria-label="First page"
          title="Go to first page"
          variant="outlinedIcon"
        >
          <Icon
            iconName="CaretLeftDouble"
            color="var(--text-black)"
            width="1rem"
            height="1rem"
          />
        </Button>
        <Button
          onClick={handlePrevClick}
          disabled={currentPage === 1 || isLoading}
          aria-label="Previous page"
          title="Go to previous page"
          variant="outlinedIcon"
        >
          <Icon
            iconName="CaretLeft"
            color="var(--text-black)"
            width="1rem"
            height="1rem"
          />
        </Button>

        <div className={styles.currentPageContainer}>
          <Button
            disabled={isLoading}
            onClick={handleCurrentPageClick}
            aria-label={`Current page: ${currentPage}`}
            title={`Page ${currentPage} of ${totalPages}`}
            variant="outlinedIcon"
            className={clsx(styles.currentPageButton)}
          >
            {isLoading ? <Spinner size="1rem" /> : currentPage}
          </Button>
          <PageJumpPopper
            isOpen={isPopperOpen}
            onClose={handlePopperClose}
            onGoToPage={handleGoToPage}
            totalPages={totalPages}
            currentPage={currentPage}
            anchorEl={anchorEl}
          />
        </div>

        <Button
          onClick={handleNextClick}
          disabled={currentPage === totalPages || isLoading}
          aria-label="Next page"
          title="Go to next page"
          variant="outlinedIcon"
        >
          <Icon
            iconName="CaretRight"
            color="var(--text-black)"
            width="1rem"
            height="1rem"
          />
        </Button>
        <Button
          onClick={handleLastClick}
          disabled={currentPage === totalPages || isLoading}
          aria-label="Last page"
          title="Go to last page"
          variant="outlinedIcon"
        >
          <Icon
            iconName="CaretRightDouble"
            color="var(--text-black)"
            width="1rem"
            height="1rem"
          />
        </Button>

        <span className={styles.pageInfo}>
          {`${startItem}-${endItem} from ${totalItems}`}
        </span>
      </div>

      {!hideItemsPerPage && (
        <div className={styles.itemsPerPage}>
          <label htmlFor="items-per-page">Rows per Page</label>
          <Select
            onChange={(e) =>
              onItemsPerPageChange?.(
                Number(
                  (e?.nativeEvent?.target as HTMLSelectElement)?.textContent,
                ) || 10,
              )
            }
            defaultValue={String(itemsPerPage)}
            disabled={isLoading}
            className={styles.select}
          >
            {options.map((option) => (
              <Option key={option.label} value={option.label}>
                {option.label}
              </Option>
            ))}
          </Select>
        </div>
      )}
    </div>
  );
};
